/* The site, page by page, with what each one needs.

   With a site map the list IS the site map: every page in site order under
   its own name, scored or not, so a score is read against the page it
   belongs to rather than inferred from a URL in small type. "Needs work
   first" is one click away for the afternoon's six. Each row says what kind
   of work it needs — because a page missing things the Vault already holds
   is a WRITING job, and a page whose gaps nobody has answered is an ASKING
   job, and sending the second to a rewriter produces padding.

   The scan runs in batches with the count in front of you. A button that
   silently works through two hundred pages is one people are afraid to press. */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { getPageHealth, scanSite, setPageSkipped, adoptSitePage, type PageHealthRow, type PageHealthOrphan } from '@/data/client';
import { PageWorksheet } from '@/components/PageWorksheet';

const OK = 'var(--ok, #15803d)';
const WARN = 'var(--warn, #b45309)';
const BAD = 'var(--danger, #b91c1c)';

const band = (n: number) => (n >= 7 ? OK : n >= 4 ? WARN : BAD);

type Order = 'site' | 'work';

const TYPE_WORD: Record<string, string> = { home: 'Home', service: 'Service', location: 'Location', about: 'About', contact: 'Contact', other: 'Other' };

function Row({ p, onSkip, onOpen, busy, indent }: { p: PageHealthRow; onSkip: (skip: boolean) => void; onOpen: () => void; busy: boolean; indent: boolean }) {
  const r = p.readiness;
  return (
    <li style={{ display: 'grid', gridTemplateColumns: '42px 1fr auto', gap: 10, alignItems: 'baseline', padding: '7px 0', borderTop: '1px solid var(--border, #e5e7eb)' }}>
      <span style={{ fontWeight: 700, fontSize: 16, color: p.rank === null ? 'var(--border, #9ca3af)' : band(p.rank) }}>
        {p.rank === null ? '—' : p.rank}
      </span>
      <span style={{ minWidth: 0, paddingLeft: indent ? p.depth * 18 : 0 }}>
        {/* The row IS the way in. A separate "open" button would make the
            obvious click the wrong one. The name is the site map's; the route
            sits beside it in the same weight, because it is how a page is
            identified, not a footnote. */}
        <button type="button" onClick={onOpen}
          style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          {p.name || p.title || p.route}
        </button>
        {' '}<code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.route}</code>
        {p.pageType && <span className="smb__type" style={{ marginLeft: 8 }}>{TYPE_WORD[p.pageType] ?? p.pageType}</span>}
        {(p.keywords || p.topic) && (
          <div className="dash-muted" style={{ fontSize: 12 }}>
            {p.keywords && <>for {p.keywords}</>}
            {p.keywords && p.topic && ' · '}
            {p.topic && <>reads as {p.topic.toLowerCase()}</>}
          </div>
        )}
      </span>
      <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {/* A page that is not on the site yet: nothing to score, and the
            worksheet opens to its brief and open questions instead. */}
        {!p.written && <Badge variant="outline">Not written yet</Badge>}
        {/* The readiness the Services ledger gives this page's service. */}
        {r && (
          <span title={`${r.service}: ${r.covered.length} of ${r.covered.length + r.open.length} interview slots covered`}>
            <Badge variant={r.state === 'ready' ? 'success' : 'warning'}>
              {r.state === 'ready' ? 'Interviews ready' : r.state === 'thin' ? 'Thin interviews' : 'No interviews'}
            </Badge>
          </span>
        )}
        {/* The Vault has moved since this page was read, so its findings are
            a stale answer to a question that now has a different one. */}
        {p.stale && <Badge variant="neutral">New Vault material</Badge>}
        {/* No danger variant exists, and a contradiction is the loudest finding
            here, so it carries its own colour rather than borrowing warning
            from the additions beside it. */}
        {p.conflicts > 0 && (
          <span style={{ background: BAD, color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
            {p.conflicts} contradiction{p.conflicts === 1 ? '' : 's'}
          </span>
        )}
        {/* Two different kinds of work, kept visibly apart. */}
        {p.missing > 0 && <Badge variant="warning">{p.missing} to add</Badge>}
        {p.unanswered > 0 && <Badge variant="outline">{p.unanswered} to ask</Badge>}
        {p.scoredAt && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onSkip(true)}
            title="This page cannot be judged on effort — take it out of the list">
            Don't score
          </Button>
        )}
      </span>
    </li>
  );
}

/* Worst first: lowest rank, then the unscored written pages, then the
   unwritten ones last — a page nobody has written is not work for this
   screen. */
function byWork(a: PageHealthRow, b: PageHealthRow): number {
  const tier = (p: PageHealthRow) => (!p.written ? 3 : p.rank === null ? 2 : p.rank < 4 ? 0 : 1);
  return tier(a) - tier(b) || ((a.rank ?? 99) - (b.rank ?? 99)) || a.route.localeCompare(b.route);
}

export function PageHealth() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['pageHealth'], queryFn: getPageHealth, enabled: isAdmin });
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [stop, setStop] = useState(false);
  const [progress, setProgress] = useState('');
  const [err, setErr] = useState('');
  const [showSkipped, setShowSkipped] = useState(false);
  const [openRoute, setOpenRoute] = useState('');
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState<Order>('site');
  const [typeFilter, setTypeFilter] = useState('');

  const skip = async (route: string, next: boolean) => {
    setBusy(true); setErr('');
    try { await setPageSkipped(route, next); await qc.invalidateQueries({ queryKey: ['pageHealth'] }); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setBusy(false); }
  };
  /* A page the scan found that the site map lacks goes onto the map as live,
     under its nearest planned ancestor; both lists are reread. */
  const adopt = async (o: PageHealthOrphan) => {
    setBusy(true); setErr('');
    try {
      await adoptSitePage(o.route);
      await Promise.all([qc.invalidateQueries({ queryKey: ['pageHealth'] }), qc.invalidateQueries({ queryKey: ['siteMap'] })]);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setBusy(false); }
  };

  if (!isAdmin) return null;
  /* The worksheet takes over the panel rather than opening beside it: reading
     findings and acting on them is one task, and half a list behind it is
     just something to lose your place in. */
  if (openRoute) return <PageWorksheet route={openRoute} onBack={() => setOpenRoute('')} />;
  const all = data?.pages ?? [];
  const registry = !!data?.registry;
  const orphans = data?.orphans ?? [];
  const types = [...new Set(all.map((p) => p.pageType).filter((t): t is string => !!t))];
  const filtered = typeFilter ? all.filter((p) => p.pageType === typeFilter) : all;
  const pages = order === 'work' && registry ? [...filtered].sort(byWork) : filtered;
  const skipped = data?.skipped ?? [];
  const conflicts = data?.conflicts ?? [];
  const scored = all.filter((p) => p.scoredAt).length;
  const unwritten = all.filter((p) => !p.written).length;
  /* Collapsed from the start, even with nothing scanned. The header says what
     the panel is for, which is invitation enough — and contentIQ is long, so a
     panel that opens itself costs every other screen on the page. */
  const expanded = open;
  const worst = all.filter((p) => p.rank !== null && p.rank < 4).length;
  const staleCount = all.filter((p) => p.stale).length;

  /* Looped here rather than server-side so the count is visible and the run
     can be stopped. A scan that cannot be interrupted is one nobody starts on
     a client they are unsure about. */
  const run = async () => {
    setRunning(true); setStop(false); setErr('');
    try {
      let guard = 0;
      for (;;) {
        const step = await scanSite(3);
        const scored = step.done.filter((d) => !d.skipped && !d.error).length;
        setProgress(`${step.total - step.remaining} of ${step.total} · ${scored ? `${scored} scored` : 'unchanged'}`);
        await qc.invalidateQueries({ queryKey: ['pageHealth'] });
        guard += 1;
        if (step.remaining === 0 || stop || guard > 200) break;
      }
    } catch (e) { setErr(e instanceof Error ? e.message : 'Scan failed'); }
    finally { setRunning(false); setProgress(''); }
  };

  return (
    <Card padding={false} className="dash-leads">
      <div className="dash-panelhead dash-panelhead--pad">
        <button type="button" onClick={() => setOpen((v) => !v)}
          style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', flex: 1 }}>
          <h2>
            <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
            {' '}<Icon name="search" size={16} /> Page health
            {all.length > 0 && (
              <span className="dash-muted" style={{ fontWeight: 400, fontSize: 13 }}>
                {' '}· {registry ? `${scored} of ${all.length} scored` : `${all.length} page${all.length === 1 ? '' : 's'}`}
              </span>
            )}
          </h2>
          <p>
            {all.length === 0
              ? 'Score every page of this client\'s site, and see which are worth an afternoon.'
              : [
                worst > 0 ? `${worst} scoring under 4` : scored ? 'nothing scoring under 4' : 'nothing scored yet',
                conflicts.length > 0 ? `${conflicts.length} contradiction${conflicts.length === 1 ? '' : 's'}` : '',
                staleCount > 0 ? `${staleCount} with new Vault material` : '',
                unwritten > 0 ? `${unwritten} not written yet` : '',
              ].filter(Boolean).join(' · ')}
          </p>
        </button>
        {expanded && (
          <div style={{ display: 'flex', gap: 6 }}>
            {running && <Button variant="ghost" size="sm" onClick={() => setStop(true)}>Stop</Button>}
            <Button variant="outline" size="sm" disabled={running} onClick={run}
              iconLeft={<Icon name="sparkles" size={15} />}>
              {running ? (progress || 'Scanning…') : pages.length ? 'Re-scan' : 'Scan the site'}
            </Button>
          </div>
        )}
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 16px' }}>
          {err && <p className="dash-muted" style={{ color: BAD }}>{err}</p>}
          {running && (
            <p className="dash-muted" style={{ fontSize: 12 }}>
              Pages that haven't changed are checked and skipped without being re-scored.
            </p>
          )}
          {/* Above the list, because one of these can account for a third of
              its rows — and fixing it once clears all of them. */}
          {conflicts.length > 0 && (
            <div style={{ margin: '8px 0 14px' }}>
              <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 4px' }}>
                Contradictions ({conflicts.length})
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {conflicts.map((cf) => (
                  <li key={cf.key} style={{ fontSize: 13, padding: '5px 0' }}>
                    <span style={{ color: BAD }}>{cf.what}</span>
                    <div className="dash-muted" style={{ fontSize: 12 }}>
                      {cf.between.join(' vs ')}
                      {' · '}
                      <strong>{cf.routes.length} page{cf.routes.length === 1 ? '' : 's'}</strong>
                      {' affected — one fix clears them all'}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isLoading && <p className="dash-muted">Loading…</p>}
          {!isLoading && all.length === 0 && !running && (
            <p className="dash-muted" style={{ fontSize: 13 }}>
              {registry
                ? 'No pages on the site map yet. Build it under contentIQ → Vault and the list appears here.'
                : 'Nothing scanned yet. The scan reads this client\'s sitemap from the Vault — if it\'s stale, re-import it first or the scan inherits the gaps.'}
            </p>
          )}
          {/* Site order is the default: the list reads as the site. The
              afternoon's worklist is the other order, one click away. */}
          {registry && all.length > 0 && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', margin: '6px 0 8px', fontSize: 12 }}>
              <button type="button" className={`vault-tab ${order === 'site' ? 'is-on' : ''}`} onClick={() => setOrder('site')}>Site order</button>
              <button type="button" className={`vault-tab ${order === 'work' ? 'is-on' : ''}`} onClick={() => setOrder('work')}>Needs work first</button>
              {types.length > 1 && (
                <>
                  <span className="dash-muted" style={{ marginLeft: 8 }}>Show</span>
                  <button type="button" className={`vault-tab ${typeFilter === '' ? 'is-on' : ''}`} onClick={() => setTypeFilter('')}>All</button>
                  {types.map((t) => (
                    <button key={t} type="button" className={`vault-tab ${typeFilter === t ? 'is-on' : ''}`} onClick={() => setTypeFilter(t)}>
                      {TYPE_WORD[t] ?? t}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
          {pages.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {pages.map((p) => (
                <Row key={p.route} p={p} busy={busy} indent={registry && order === 'site' && !typeFilter}
                  onSkip={(v) => skip(p.route, v)} onOpen={() => setOpenRoute(p.route)} />
              ))}
            </ul>
          )}

          {/* Pages the scan read that the site map does not have: an old
              sitemap, or a page somebody published without planning it. Each
              one is a click from being on the map. */}
          {orphans.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 2px' }}>Not on the site map ({orphans.length})</p>
              <p className="dash-muted" style={{ fontSize: 12, margin: '0 0 4px' }}>
                Scored on an earlier scan, but the site map has no page at these routes.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {orphans.map((o) => (
                  <li key={o.route} style={{ display: 'grid', gridTemplateColumns: '42px 1fr auto', gap: 10, alignItems: 'baseline', padding: '6px 0', borderTop: '1px solid var(--border, #e5e7eb)' }}>
                    <span style={{ fontWeight: 700, fontSize: 16, color: o.rank === null ? 'var(--border, #9ca3af)' : band(o.rank) }}>{o.rank ?? '—'}</span>
                    <span style={{ minWidth: 0 }}>
                      <button type="button" onClick={() => setOpenRoute(o.route)}
                        style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        {o.title || o.route}
                      </button>
                      {' '}<code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{o.route}</code>
                    </span>
                    <span style={{ display: 'flex', gap: 6 }}>
                      <Button variant="outline" size="sm" disabled={busy} onClick={() => adopt(o)}>Add to site map</Button>
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => skip(o.route, true)}>Don't score</Button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Visible, so an exclusion can be reversed and so nobody wonders
              where a page went. */}
          {skipped.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <button type="button" onClick={() => setShowSkipped((v) => !v)}
                className="dash-muted"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12 }}>
                <Icon name={showSkipped ? 'chevron-down' : 'chevron-right'} size={12} />
                {' '}Not scored ({skipped.length}) — skipped on future scans
              </button>
              {showSkipped && (
                <ul style={{ listStyle: 'none', padding: '6px 0 0', margin: 0 }}>
                  {skipped.map((p) => (
                    <li key={p.route} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, padding: '3px 0' }}>
                      <span className="dash-muted">{p.title || p.route}</span>
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => skip(p.route, false)}>
                        Score it again
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
